"use client";

import { useEffect, useState, useMemo, useCallback } from "react";
import { format } from "date-fns";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/app/_module/components/ui/table";
import { Badge } from "@/app/_module/components/ui/badge";
import { Button } from "@/app/_module/components/ui/button";
import { Input } from "@/app/_module/components/ui/input";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/app/_module/components/ui/select";
import {
  Dialog,
  ResponsiveDialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from "@/app/_module/components/ui/dialog";
import { Textarea } from "@/app/_module/components/ui/textarea";
import { 
  AlertTriangle, 
  Eye, 
  CheckCircle, 
  XCircle, 
  Search, 
  Filter,
  MoreHorizontal,
  User,
  Mail,
  Phone,
  Calendar,
  Clock
} from "lucide-react";
import { 
  duplicateAccountService, 
  type DuplicateAccountFlag, 
  type DuplicateAccountFilters 
} from "@/app/_module/services/duplicateAccountService";
import LoadingSpinner from "@/app/_module/components/LoadingSpinner";
import { useToast } from "@/app/_module/hooks/use-toast";

interface DuplicateAccountFlagsTableProps {
  onFlagSelect?: (flag: DuplicateAccountFlag) => void;
}

export default function DuplicateAccountFlagsTable({ onFlagSelect }: DuplicateAccountFlagsTableProps) {
  const [flags, setFlags] = useState<DuplicateAccountFlag[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<DuplicateAccountFilters>({
    page: 1,
    limit: 10,
  });
  const [totalPages, setTotalPages] = useState(1);
  const [selectedFlag, setSelectedFlag] = useState<DuplicateAccountFlag | null>(null);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [adminNotes, setAdminNotes] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const { toast } = useToast();

  // Memoize filters to prevent infinite loops
  const memoizedFilters = useMemo(() => filters, [filters.page, filters.limit, filters.status, filters.search]);

  const fetchFlags = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await duplicateAccountService.getDuplicateAccountFlags(memoizedFilters);
      setFlags(response.flags);
      setTotalPages(response.meta.totalPages);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch flags');
    } finally {
      setIsLoading(false);
    }
  }, [memoizedFilters]);

  useEffect(() => {
    fetchFlags();
  }, [fetchFlags]);

  const handleStatusChange = (status: string) => {
    // Handle "all" as clearing the status filter
    setFilters(prev => ({ ...prev, status: status === "all" ? undefined : status as any, page: 1 }));
  };

  const handleSearch = (search: string) => {
    setFilters(prev => ({ ...prev, search, page: 1 }));
  };

  const handlePageChange = (page: number) => {
    setFilters(prev => ({ ...prev, page }));
  };

  const handleFlagClick = (flag: DuplicateAccountFlag) => {
    setSelectedFlag(flag);
    setShowDetailsDialog(true);
    onFlagSelect?.(flag);
  };

  const handleReview = async () => {
    if (!selectedFlag || !adminNotes.trim()) return;

    try {
      setIsProcessing(true);
      await duplicateAccountService.reviewFlag(selectedFlag.id, adminNotes);
      toast({
        title: "Flag Reviewed",
        description: "The flag has been marked as reviewed.",
      });
      setShowDetailsDialog(false);
      setAdminNotes("");
      fetchFlags();
    } catch (err) {
      toast({
        title: "Error",
        description: "Failed to review flag. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleResolve = async (action: 'merge' | 'separate' | 'suspend_duplicate' | 'no_action') => {
    if (!selectedFlag || !adminNotes.trim()) return;

    try {
      setIsProcessing(true);
      await duplicateAccountService.resolveFlag(selectedFlag.id, action, adminNotes);
      toast({
        title: "Flag Resolved",
        description: `The flag has been resolved with action: ${action.replace('_', ' ')}`,
      });
      setShowDetailsDialog(false);
      setAdminNotes("");
      fetchFlags();
    } catch (err) {
      toast({
        title: "Error",
        description: "Failed to resolve flag. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDismiss = async () => {
    if (!selectedFlag || !adminNotes.trim()) return;

    try {
      setIsProcessing(true);
      await duplicateAccountService.dismissFlag(selectedFlag.id, adminNotes);
      toast({
        title: "Flag Dismissed",
        description: "The flag has been dismissed.",
      });
      setShowDetailsDialog(false);
      setAdminNotes("");
      fetchFlags();
    } catch (err) {
      toast({
        title: "Error",
        description: "Failed to dismiss flag. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      pending: { color: "bg-yellow-100 text-yellow-800", icon: Clock },
      reviewed: { color: "bg-blue-100 text-blue-800", icon: Eye },
      resolved: { color: "bg-green-100 text-green-800", icon: CheckCircle },
      dismissed: { color: "bg-gray-100 text-gray-800", icon: XCircle },
    };

    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.pending;
    const Icon = config.icon;

    return (
      <Badge className={config.color}>
        <Icon className="w-3 h-3 mr-1" />
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center p-8">
        <p className="text-red-600">Error: {error}</p>
        <Button onClick={fetchFlags} className="mt-4">
          Try Again
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <Input
              placeholder="Search by name, email, NPN, or phone..."
              value={filters.search || ""}
              onChange={(e) => handleSearch(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>
        <Select value={filters.status || "all"} onValueChange={handleStatusChange}>
          <SelectTrigger className="w-full sm:w-48">
            <Filter className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="reviewed">Reviewed</SelectItem>
            <SelectItem value="resolved">Resolved</SelectItem>
            <SelectItem value="dismissed">Dismissed</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Flagged User</TableHead>
              <TableHead>Existing User</TableHead>
              <TableHead>Duplicate Info</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Created</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {flags.map((flag) => (
              <TableRow key={flag.id} className="hover:bg-gray-50">
                <TableCell>
                  <div className="space-y-1">
                    <div className="font-medium">{flag.flaggedUser.name}</div>
                    <div className="text-sm text-gray-500 flex items-center">
                      <Mail className="w-3 h-3 mr-1" />
                      {flag.flaggedUser.email}
                    </div>
                    <div className="text-sm text-gray-500 flex items-center">
                      <Phone className="w-3 h-3 mr-1" />
                      {flag.flaggedUser.phone}
                    </div>
                  </div>
                </TableCell>
                <TableCell>
                  <div className="space-y-1">
                    <div className="font-medium">{flag.existingUser.name}</div>
                    <div className="text-sm text-gray-500 flex items-center">
                      <Mail className="w-3 h-3 mr-1" />
                      {flag.existingUser.email}
                    </div>
                    <div className="text-sm text-gray-500 flex items-center">
                      <Phone className="w-3 h-3 mr-1" />
                      {flag.existingUser.phone}
                    </div>
                  </div>
                </TableCell>
                <TableCell>
                  <div className="space-y-1">
                    <div className="text-sm">
                      <span className="font-medium">NPN:</span> {flag.duplicateNpn}
                    </div>
                    <div className="text-sm text-gray-500">
                      {flag.flagReason}
                    </div>
                  </div>
                </TableCell>
                <TableCell>
                  {getStatusBadge(flag.status)}
                </TableCell>
                <TableCell>
                  <div className="text-sm text-gray-500 flex items-center">
                    <Calendar className="w-3 h-3 mr-1" />
                    {format(new Date(flag.createdAt), 'MMM dd, yyyy')}
                  </div>
                </TableCell>
                <TableCell>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleFlagClick(flag)}
                  >
                    <Eye className="w-4 h-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-500">
            Page {filters.page} of {totalPages}
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange((filters.page || 1) - 1)}
              disabled={!filters.page || filters.page <= 1}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange((filters.page || 1) + 1)}
              disabled={!filters.page || filters.page >= totalPages}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* Flag Details Dialog */}
      <Dialog open={showDetailsDialog} onOpenChange={setShowDetailsDialog}>
        <ResponsiveDialogContent size="3xl" className="max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center">
              <AlertTriangle className="w-5 h-5 mr-2 text-yellow-600" />
              Duplicate Account Flag Details
            </DialogTitle>
            <DialogDescription>
              Review the duplicate account information and take appropriate action.
            </DialogDescription>
          </DialogHeader>

          {selectedFlag && (
            <div className="space-y-6">
              {/* Flag Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-red-600">Flagged User</h3>
                  <div className="space-y-2">
                    <div><strong>Name:</strong> {selectedFlag.flaggedUser.name}</div>
                    <div><strong>Email:</strong> {selectedFlag.flaggedUser.email}</div>
                    <div><strong>NPN:</strong> {selectedFlag.flaggedUser.npn}</div>
                    <div><strong>Phone:</strong> {selectedFlag.flaggedUser.phone}</div>
                    <div><strong>Created:</strong> {format(new Date(selectedFlag.flaggedUser.createdAt), 'MMM dd, yyyy HH:mm')}</div>
                  </div>
                </div>

                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-blue-600">Existing User</h3>
                  <div className="space-y-2">
                    <div><strong>Name:</strong> {selectedFlag.existingUser.name}</div>
                    <div><strong>Email:</strong> {selectedFlag.existingUser.email}</div>
                    <div><strong>NPN:</strong> {selectedFlag.existingUser.npn}</div>
                    <div><strong>Phone:</strong> {selectedFlag.existingUser.phone}</div>
                    <div><strong>Created:</strong> {format(new Date(selectedFlag.existingUser.createdAt), 'MMM dd, yyyy HH:mm')}</div>
                  </div>
                </div>
              </div>

              {/* Flag Details */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">Flag Information</h3>
                <div className="space-y-2">
                  <div><strong>Duplicate NPN:</strong> {selectedFlag.duplicateNpn}</div>
                  <div><strong>Reason:</strong> {selectedFlag.flagReason}</div>
                  <div><strong>Status:</strong> {getStatusBadge(selectedFlag.status)}</div>
                  <div><strong>Flagged:</strong> {format(new Date(selectedFlag.createdAt), 'MMM dd, yyyy HH:mm')}</div>
                </div>
              </div>

              {/* Admin Notes */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">Admin Notes</h3>
                <Textarea
                  placeholder="Add your notes about this flag..."
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  rows={3}
                />
              </div>

              {/* Actions */}
              <div className="flex flex-wrap gap-2">
                <Button
                  onClick={handleReview}
                  disabled={isProcessing || !adminNotes.trim()}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  <Eye className="w-4 h-4 mr-2" />
                  Mark as Reviewed
                </Button>
                
                <Button
                  onClick={() => handleResolve('merge')}
                  disabled={isProcessing || !adminNotes.trim()}
                  className="bg-green-600 hover:bg-green-700"
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Merge Accounts
                </Button>
                
                <Button
                  onClick={() => handleResolve('separate')}
                  disabled={isProcessing || !adminNotes.trim()}
                  className="bg-yellow-600 hover:bg-yellow-700"
                >
                  <User className="w-4 h-4 mr-2" />
                  Keep Separate
                </Button>
                
                <Button
                  onClick={() => handleResolve('suspend_duplicate')}
                  disabled={isProcessing || !adminNotes.trim()}
                  className="bg-red-600 hover:bg-red-700"
                >
                  <XCircle className="w-4 h-4 mr-2" />
                  Suspend Duplicate
                </Button>
                
                <Button
                  onClick={handleDismiss}
                  disabled={isProcessing || !adminNotes.trim()}
                  variant="outline"
                >
                  <XCircle className="w-4 h-4 mr-2" />
                  Dismiss Flag
                </Button>
              </div>
            </div>
          )}
        </ResponsiveDialogContent>
      </Dialog>
    </div>
  );
}
